import { Button, Col, DatePicker, Form, notification, Row, Space, Typography } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import CustomSelect from '../../../components/CustomSelect';
import FilterTable from '../../../components/FilterTable';
import { dataToOptionSelect, exportTableToExcelNew, formatNumber, newObjectKeys } from '../../../utils/utility';
import BieuDo from '../../../components/LineChart';
import {
  getDataStatisticVideoRequest,
  getGraphicalStatisticVideoRequest,
} from '../../../services/lichvietvideo/videoStatisticServiceRequest';
import {
  getAllVideosRequest,
  getListDimensionsAndMetricsRequest,
  getListDimensionsRequest,
  getListMetricsRequest,
} from '../../../services/lichvietvideo/commonServiceRequest';

const rangePresets = [
  {
    label: '7 ngày trước',
    value: [dayjs().subtract(7, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '14 ngày trước',
    value: [dayjs().subtract(14, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '30 ngày trước',
    value: [dayjs().subtract(30, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '90 ngày trước',
    value: [dayjs().subtract(90, 'days'), dayjs().subtract(1, 'days')],
  },
];

export default function ThongKeVideo() {
  const [formSearch] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingExportData, setIsLoadingExportData] = useState(false);

  const [params, setParams] = useState({
    start_time: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
    end_time: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
    video_ids: [],
  });

  const [filterDimensionAndMetric, setFilterDimensionAndMetric] = useState({
    pageId: 1,
    limit: 20,
    dimension_ids: [1, 2],
    metric_ids: [1, 2, 3],
    order_by_conditions: [],
  });

  const [{ data: dataAllVideos }, { data: dataDimensionsAndMetrics }] = useQueries({
    queries: [
      {
        queryKey: ['dataAllVideosStatistic'],
        queryFn: () => getAllVideosRequest(),
      },

      {
        queryKey: ['dataDimensionsAndMetricsStatistic'],
        queryFn: () => getListDimensionsAndMetricsRequest(),
      },
    ],
  });

  useEffect(() => {
    if (dataDimensionsAndMetrics) {
      const { dimensions, metrics } = dataDimensionsAndMetrics;
      setFilterDimensionAndMetric((prev) => ({
        ...prev,
        dimension_ids: dimensions.map((item) => item.id),
        metric_ids: metrics.map((item) => item.id),
      }));
    }
  }, [dataDimensionsAndMetrics]);

  const {
    data: dataStatisticVideos,
    refetch: refetchDataStatisticVideos,
    isPending: isPendingDataStatisticVideos,
  } = useQuery({
    queryKey: ['dataStatisticVideos', { ...params, ...filterDimensionAndMetric }],
    queryFn: () =>
      getDataStatisticVideoRequest({
        ...params,
        ...filterDimensionAndMetric,
        page: filterDimensionAndMetric?.pageId,
        pageId: undefined,
      }),
  });

  const handleSubmitForm = (values) => {
    const temp = {};

    if (values?.timeRange) {
      Object.assign(temp, {
        start_time: dayjs(values?.timeRange?.[0]).format('YYYY-MM-DD'),
        end_time: dayjs(values?.timeRange?.[1]).format('YYYY-MM-DD'),
      });
    }

    if (values?.video_ids) {
      Object.assign(temp, { video_ids: values?.video_ids });
    } else {
      Object.assign(temp, { video_ids: [] });
    }

    if (isEqual(temp, params)) {
      refetchDataStatisticVideos();
    }
    setParams(temp);
    setFilterDimensionAndMetric({ ...filterDimensionAndMetric, pageId: 1, limit: 20 });
  };

  const handleExportData = async () => {
    setIsLoadingExportData(true);
    const dataExport = await getDataStatisticVideoRequest({
      ...params,
      ...filterDimensionAndMetric,
      page: 1,
      pageId: undefined,
      limit: dataStatisticVideos?.totalItems || 1000000, // Set a high limit to export all data
    });

    if (!dataExport?.data?.statistics || dataExport?.data?.statistics.length === 0) {
      notification.error({
        message: 'Xuất file dữ liệu thất bại. Vui lòng thử lại sau!',
      });
      setIsLoadingExportData(false);
      return;
    }

    let dataExportTmp = [];

    dataExport?.data?.statistics?.forEach((statistic, i) => {
      const value = statistic.reduce((acc, item) => {
        acc[item.name] = formatNumber(item.value);
        acc.type = item.type;
        acc.id = item.id;
        acc.key = i;
        return acc;
      }, {});
      dataExportTmp.push(value);
    });

    const fixedColumns = dataExport?.data?.total || [];
    const colSpan = dataExport?.data?.statistics[0].filter((item) => item.type === 'dimension')?.length || 0;

    const dataExportFinal = {
      data: dataExportTmp,
      fixedColumns: fixedColumns,
      colSpan: colSpan,
    };

    exportTableToExcelNew(
      dataExportFinal,
      `Thongkevideo_tungay_${dayjs(params?.start_time).format('DD-MM-YYYY')}_denngay_${dayjs(params?.end_time).format(
        'DD-MM-YYYY'
      )}`
    );
    setIsLoadingExportData(false);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="font-semibold text-2xl font-roboto">Thống kê video</h1>
        <div className="flex justify-end gap-2">
          <Button loading={isLoadingExportData} type="primary" onClick={() => handleExportData()}>
            <DownloadOutlined /> Export
          </Button>

          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            <PlusOutlined /> Thêm biểu đồ
          </Button>
        </div>
      </div>

      <Form layout="vertical" name="search-Videos" form={formSearch} onFinish={handleSubmitForm}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} lg={6}>
            <Form.Item
              label={<Typography.Text>Thời gian</Typography.Text>}
              name="timeRange"
              initialValue={[dayjs(params?.start_time, 'YYYY-MM-DD'), dayjs(params?.end_time, 'YYYY-MM-DD')]}
            >
              <DatePicker.RangePicker
                placeholder={['Từ ngày', 'Đến ngày']}
                format="DD-MM-YYYY"
                presets={rangePresets}
                allowClear={false}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<Typography.Text>Video</Typography.Text>} name="video_ids">
              <CustomSelect
                placeholder="Video"
                options={dataToOptionSelect(dataAllVideos?.data || [])}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item>
              <Space direction="horizontal" className="sm:mt-7">
                <Button type="primary" htmlType="submit">
                  <SearchOutlined /> Tìm kiếm
                </Button>
                {params &&
                  Object.values(newObjectKeys(params, ['start_time', 'end_time', 'video_ids'])).some(
                    (value) => (value && !Array.isArray(value)) || value?.length > 0
                  ) && (
                    <Button
                      htmlType="button"
                      onClick={() => {
                        formSearch.resetFields();
                        formSearch.setFieldValue('timeRange', [
                          dayjs().subtract(30, 'days'),
                          dayjs().subtract(1, 'days'),
                        ]);
                        setParams({
                          start_time: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
                          end_time: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
                          video_ids: [],
                        });
                        setFilterDimensionAndMetric({ ...filterDimensionAndMetric, pageId: 1, limit: 20 });
                      }}
                    >
                      <DeleteOutlined />
                      Xóa lọc
                    </Button>
                  )}
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BieuDo
        whereCondition={params}
        apiCallBack={getGraphicalStatisticVideoRequest}
        dataDimensions={dataDimensionsAndMetrics?.dimensions}
        dataMetrics={dataDimensionsAndMetrics?.metrics}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />

      <div className="flex w-full">
        <FilterTable
          isLoadingFilterTable={isPendingDataStatisticVideos}
          filterDimensionAndMetric={filterDimensionAndMetric}
          setFilterDimensionAndMetric={setFilterDimensionAndMetric}
          dataDimensions={dataDimensionsAndMetrics?.dimensions}
          dataMetrics={dataDimensionsAndMetrics?.metrics}
          dataStatistics={dataStatisticVideos}
        />
      </div>
    </div>
  );
}
